import React from 'react';

import {
  ActionIcon,
  AutocompleteItem,
  Checkbox,
  Divider,
  Group,
  Input,
  NumberInput,
  Paper,
  Radio,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { DateInput, TimeInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDisclosure, useShallowEffect } from '@mantine/hooks';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { AxiosError, AxiosResponse } from 'axios';
import ExpandableSection from 'components/ExpandableSection/ExpandableSection';
import AppointmentCoachRoleAutoComplete, {
  AppointmentCoachRoleAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/AppointmentCoachRoleAutoComplete';
import AppointmentTypeAutoComplete, {
  AppointmentTypeAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/AppointmentTypeAutoComplete';
import AthleteMultiSelectAutoComplete from 'components/form-fields/auto-complete-fields/AthleteMultiSelectAutoComplete';
import LocationAutoComplete, {
  LocationAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/LocationAutoComplete';
import StaffAutoComplete, {
  StaffAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/StaffAutoComplete';
import MoneyField from 'components/MoneyField';
import { useLocalization } from 'context/localization-context';
import dayjs, { Dayjs } from 'dayjs';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { UseMutationResult } from 'react-query';
import { RRuleSet, rrulestr } from 'rrule';
import { Appointment, AppointmentRetrieve, AppointmentWrite, DayAbbreviation, ErrorMessage } from 'types';
import { TimeUnitEnum } from 'types/enums';
import { getChangedKeysForAppointmentEdit } from 'utils/analytics-selectors';
import {
  calculateEventEndDateTime,
  createRrule,
  getDayAbbreviationFromDate,
  getDayAbbreviationsFromRruleBydays,
  getDayDiff,
  parseRruleForFrequency,
  parseRruleForMonthDay,
  type RecurrenceFrequency,
} from 'utils/event.helpers';
import { setMaxLateCancellationIntervalCount } from 'utils/functions';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

interface FormValues {
  appointmentId: string;
  startDate: Date;
  startTime: string;
  duration: number;
  endDate: Date | null;
  appointmentType: AppointmentTypeAutoCompleteValue;
  appointmentCoachAppointmentCoachRoles: {
    appointmentCoachAppointmentCoachRoleId: string;
    coach: StaffAutoCompleteValue;
    appointmentCoachRole: AppointmentCoachRoleAutoCompleteValue;
  }[];
  location: LocationAutoCompleteValue;
  rootAppointment: string | null;
  days: DayAbbreviation[];
  frequency: RecurrenceFrequency;
  cancellationSettings: {
    useLateCancellationFee: boolean;
    useNoShowFee: boolean;
    allowLateCancellation: boolean;
    cancellationInterval: 'minute' | 'hour' | 'day';
    cancellationIntervalCount: number;
    cancellationFee: number;
    cancellationCreditDeduction: boolean;
    noShowFee: number;
    noShowCreditDeduction: boolean;
  };
  appointmentEnd: Dayjs | null;
  dayDiff: string;
  athletes: string[];
}

interface IDetailsEditAllFutureAppointmentsFormProps {
  appointment: AppointmentRetrieve;
  closeHandler: () => void;
  formId: string;
  updateMutation: UseMutationResult<AxiosResponse<Appointment>, AxiosError<ErrorMessage>, AppointmentWrite>;
  prefillStartTime?: string;
  prefillOriginalCoachId?: string;
  prefillNewCoachId?: string;
  prefillNewCoachName?: string;
}

const timeUnitOptions = [
  { value: TimeUnitEnum.MINUTE, label: 'Min(s)' },
  { value: TimeUnitEnum.HOUR, label: 'Hour(s)' },
  { value: TimeUnitEnum.DAY, label: 'Day(s)' },
];

const DetailsEditAllFutureAppointmentsForm = ({
  appointment,
  closeHandler,
  formId,
  updateMutation,
  prefillStartTime,
  prefillOriginalCoachId,
  prefillNewCoachId,
  prefillNewCoachName,
}: IDetailsEditAllFutureAppointmentsFormProps): JSX.Element => {
  const theme = useMantineTheme();
  const { country } = useLocalization();
  const staffTracking = useStaffTracking();

  const [settingsOpened, { toggle: handleSettingsToggle }] = useDisclosure();

  const effectiveStartTime = prefillStartTime ?? appointment.startTime;

  const initialDays = appointment.rootAppointment?.rrule
    ? getDayAbbreviationsFromRruleBydays(
        (rrulestr(appointment.rootAppointment.rrule, { forceset: true }) as RRuleSet)._rrule[0].options
          .byweekday,
      )
    : [getDayAbbreviationFromDate(dayjs(appointment.startDate).toDate())];

  // Preserve the series' original monthly anchor day. `startDate` here is the clicked
  // occurrence and is rendered disabled, so on a clamped occurrence (Feb 28 of a 31st
  // series) it would otherwise re-anchor the whole series to the 28th on save.
  const initialMonthDay = parseRruleForMonthDay(appointment.rootAppointment?.rrule);

  const cancellationSettings = appointment.cancellationSettings;

  const initialAppointmentEnd = calculateEventEndDateTime(
    appointment.duration,
    effectiveStartTime,
    dayjs(appointment.startDate).toDate(),
  );

  const newCoachAlreadyOnEvent =
    prefillNewCoachId != null &&
    appointment.appointmentCoachAppointmentCoachRoles.some(r => r.coach.staffId === prefillNewCoachId);

  const formValues: FormValues = {
    ...appointment,
    startDate: dayjs(appointment.startDate).toDate(),
    endDate: appointment.rootAppointment?.endDate
      ? dayjs(appointment.rootAppointment.endDate).toDate()
      : null,
    startTime: dayjs(`${appointment.startDate} ${effectiveStartTime}`).format('HH:mm').toString(),
    appointmentType: {
      appointmentTypeId: appointment.appointmentType?.appointmentTypeId || '',
      value: appointment.appointmentType?.appointmentTypeId || '',
      label: appointment.appointmentType?.name || '',
    },
    appointmentCoachAppointmentCoachRoles: appointment.appointmentCoachAppointmentCoachRoles
      .filter(acacr => !(acacr.coach.staffId === prefillOriginalCoachId && newCoachAlreadyOnEvent))
      .map(acacr => {
        const isSwapped =
          prefillOriginalCoachId != null &&
          prefillNewCoachId != null &&
          acacr.coach.staffId === prefillOriginalCoachId;
        const coachId = isSwapped ? prefillNewCoachId : acacr.coach.staffId;
        const coachName = isSwapped ? prefillNewCoachName ?? '' : acacr.coach.fullName;
        return {
          appointmentCoachAppointmentCoachRoleId: acacr.appointmentCoachAppointmentCoachRoleId,
          coach: {
            staffId: coachId,
            value: coachId,
            label: coachName,
          },
          appointmentCoachRole: {
            appointmentCoachRoleId: acacr.appointmentCoachRole.appointmentCoachRoleId,
            value: acacr.appointmentCoachRole.appointmentCoachRoleId,
            label: acacr.appointmentCoachRole.name,
          },
        };
      }),
    location: {
      locationId: appointment.location?.locationId || '',
      label: appointment.location?.name || '',
      value: appointment.location?.locationId || '',
    },
    rootAppointment: null,
    days: initialDays,
    frequency: parseRruleForFrequency(appointment.rootAppointment?.rrule),
    cancellationSettings: {
      useLateCancellationFee: cancellationSettings.cancellationFee !== 0,
      useNoShowFee: cancellationSettings.noShowFee !== 0,
      allowLateCancellation: cancellationSettings.allowLateCancellation ?? true,
      cancellationInterval: cancellationSettings.cancellationInterval ?? 'minute',
      cancellationIntervalCount: cancellationSettings.cancellationIntervalCount ?? 0,
      cancellationFee: cancellationSettings.cancellationFee ?? 0,
      cancellationCreditDeduction: cancellationSettings.cancellationCreditDeduction ?? false,
      noShowFee: cancellationSettings.noShowFee ?? 0,
      noShowCreditDeduction: cancellationSettings.noShowCreditDeduction ?? false,
    },
    appointmentEnd: initialAppointmentEnd,
    dayDiff: getDayDiff(dayjs(appointment.startDate), initialAppointmentEnd),
    athletes: appointment.athleteAppointments.map(athleteAppointment => {
      return athleteAppointment.athlete.athleteId;
    }),
  };

  const initialValues = formValues;

  const form = useForm<FormValues>({
    initialValues,

    validate: values => ({
      appointmentType: values.appointmentType ? null : 'Appointment type is required',
      duration: values.duration < 15 ? 'Duration must be more or equal 15 mins' : null,
      startDate: values.startDate ? null : 'Start date is required',
      startTime: values.startTime ? null : 'Start time is required',
      athletes: values.athletes.length ? null : 'Select 1 or more athletes',
    }),
  });

  const handleCloseAndFormReset = (): void => {
    closeHandler();
    form.reset();
  };

  const handleAthletesChange = (values: string[]): void => {
    form.setFieldValue('athletes', values);
  };

  const handleAddCoach = (): void => {
    form.insertListItem('appointmentCoachAppointmentCoachRoles', {
      appointmentCoachAppointmentCoachRoleId: uuidv4(),
      coach: {
        staffId: '',
        value: '',
        label: '',
      },
      appointmentCoachRole: {
        appointmentCoachRoleId: '',
        value: '',
        label: '',
      },
    });
  };

  const handleCoachChange = (item: AutocompleteItem | null, index: number): void => {
    const selectedCoach = {
      staffId: item ? item.staffId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue(`appointmentCoachAppointmentCoachRoles.${index}.coach`, selectedCoach);
  };

  const handleAppointmentCoachRoleChange = (item: AutocompleteItem | null, index: number): void => {
    const selectedAppointmentCoachRole = {
      appointmentCoachRoleId: item ? item.appointmentCoachRoleId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue(
      `appointmentCoachAppointmentCoachRoles.${index}.appointmentCoachRole`,
      selectedAppointmentCoachRole,
    );
  };

  const handleLocationChange = (item: LocationAutoCompleteValue | null): void => {
    const selectedLocation = {
      locationId: item ? item.locationId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue('location', selectedLocation);
  };

  const handleAppointmentTypeChange = (item: AppointmentTypeAutoCompleteValue | null): void => {
    const selectedAppointmentType = {
      appointmentTypeId: item ? item.appointmentTypeId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue('appointmentType', selectedAppointmentType);
  };

  const handleFrequencyChange = (value: string): void => {
    const frequency = value as RecurrenceFrequency;
    form.setFieldValue('frequency', frequency);
    // Weekly/biweekly need at least one weekday; seed the start date's weekday when
    // switching away from monthly (which carries no weekdays) so the rrule is valid.
    if (frequency !== 'monthly' && form.values.days.length === 0) {
      form.setFieldValue('days', [getDayAbbreviationFromDate(dayjs(form.values.startDate).toDate())]);
    }
  };

  const handleUpdate = (values: FormValues): void => {
    if (updateMutation.isLoading) return;
    const { errorNotification, successNotification } = notificationHandler();
    let rrule = createRrule(
      form.values.startDate,
      form.values.endDate,
      form.values.days,
      form.values.frequency,
      initialMonthDay,
    );
    const payload = {
      ...values,
      appointmentId: appointment?.rootAppointment?.appointmentId || '',
      appointmentType: values.appointmentType.appointmentTypeId,
      appointmentCoachAppointmentCoachRoles: values.appointmentCoachAppointmentCoachRoles.map(acr => ({
        appointmentCoachRole: acr.appointmentCoachRole.appointmentCoachRoleId,
        coach: acr.coach.staffId,
      })),
      location: values.location?.locationId || null,
      startDate: dayjs(values.startDate).format('YYYY-MM-DD'),
      endDate: values.endDate ? dayjs(values.endDate).format('YYYY-MM-DD') : null,
      rrule,
      cancellationSettings: {
        allowLateCancellation: values.cancellationSettings.allowLateCancellation,
        cancellationInterval: values.cancellationSettings.cancellationInterval,
        cancellationIntervalCount: values.cancellationSettings.cancellationIntervalCount,
        cancellationFee:
          values.cancellationSettings.useLateCancellationFee &&
          values.cancellationSettings.allowLateCancellation
            ? values.cancellationSettings.cancellationFee
            : 0,
        cancellationCreditDeduction: values.cancellationSettings.allowLateCancellation
          ? values.cancellationSettings.cancellationCreditDeduction
          : false,
        noShowFee: values.cancellationSettings.useNoShowFee ? values.cancellationSettings.noShowFee : 0,
        noShowCreditDeduction: values.cancellationSettings.noShowCreditDeduction,
      },
      editThisEventOnly: false,
      sendNotification: true,
    };

    const changedKeys: string[] = getChangedKeysForAppointmentEdit(initialValues, values);

    updateMutation.mutate(payload, {
      onSuccess: () => {
        successNotification(`${values.appointmentType.label} created successfully`);
        if (changedKeys.length > 0) {
          staffTracking.trackEventUpdate(
            appointment?.rootAppointment?.appointmentId || appointment.appointmentId,
            values.appointmentType.appointmentTypeId,
            values.appointmentType.label,
            'appointment',
            'all future',
            changedKeys,
          );
        }
        handleCloseAndFormReset();
      },
      onError: error => {
        errorNotification('Updating appointments failed', error.response?.data.message);
      },
    });
  };

  useShallowEffect(() => {
    if (form.values.duration && form.values.startTime) {
      const appointmentEnd = calculateEventEndDateTime(
        form.values.duration,
        form.values.startTime,
        dayjs(form.values.startDate).toDate(),
      );
      form.setFieldValue('appointmentEnd', appointmentEnd);
      form.setFieldValue('dayDiff', getDayDiff(dayjs(form.values.startDate), appointmentEnd));
    } else {
      form.setFieldValue('appointmentEnd', null);
      form.setFieldValue('dayDiff', '');
    }
  }, [form.values.duration, form.values.startTime, form.values.startDate]);

  return (
    <form id={formId} onSubmit={form.onSubmit(handleUpdate)}>
      <Stack spacing='xs'>
        <Group grow align='top'>
          <AppointmentTypeAutoComplete
            label='Appointment Type'
            placeholder='Select an appointment type'
            value={form.values.appointmentType}
            error={form.errors.appointmentType}
            onChange={handleAppointmentTypeChange}
            required
            disabled
          />
          <LocationAutoComplete
            label='Location'
            placeholder='Select a location'
            value={form.values.location}
            onChange={handleLocationChange}
            error={form.errors.location}
          />
        </Group>

        <Paper withBorder p='sm' bg='gray.0'>
          <Stack spacing='sm'>
            {!form.values.appointmentCoachAppointmentCoachRoles.length && (
              <Text size='sm' color='dimmed'>
                No Coach
              </Text>
            )}
            {form.values.appointmentCoachAppointmentCoachRoles.map((acacr, i) => (
              <Group
                key={acacr.appointmentCoachAppointmentCoachRoleId}
                position='apart'
                align='flex-start'
                noWrap
              >
                <StaffAutoComplete
                  label='Coach'
                  placeholder='Select a coach'
                  {...form.getInputProps(`appointmentCoachAppointmentCoachRoles.${i}.coach`)}
                  onChange={value => handleCoachChange(value, i)}
                  required
                  disinclude={form.values.appointmentCoachAppointmentCoachRoles.map(a => a.coach.staffId)}
                  isCoach
                />
                <AppointmentCoachRoleAutoComplete
                  label='Role'
                  placeholder='Select a role'
                  {...form.getInputProps(`appointmentCoachAppointmentCoachRoles.${i}.appointmentCoachRole`)}
                  onChange={value => handleAppointmentCoachRoleChange(value, i)}
                  required
                />
                <ActionIcon
                  mt='xl'
                  size='lg'
                  color='red'
                  onClick={() => form.removeListItem('appointmentCoachAppointmentCoachRoles', i)}
                >
                  <IconTrash size={theme.fontSizes.lg} />
                </ActionIcon>
              </Group>
            ))}
            <ActionIcon variant='transparent' color='kilo-blue' onClick={handleAddCoach}>
              <IconPlus />
            </ActionIcon>
          </Stack>
        </Paper>

        <Group grow align='top'>
          <AthleteMultiSelectAutoComplete
            {...form.getInputProps('athletes')}
            label='Athletes'
            onChange={handleAthletesChange}
            value={form.values.athletes}
          />
        </Group>

        <Divider mt='xs' mx='-xl' />

        <Group grow align='top'>
          <NumberInput
            {...form.getInputProps('duration')}
            label='Duration in minutes'
            placeholder='Enter duration'
            required
            min={15}
            max={60 * 24 * 7}
          />
          <div />
        </Group>

        <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
          <DateInput {...form.getInputProps('startDate')} label='Start Date' locale='us' required disabled />
          <Group spacing='xs' grow>
            <TimeInput label='From' {...form.getInputProps('startTime')} required />
            <TimeInput
              label={
                <Group position='apart'>
                  <div>To</div>
                  <Text color='red'>{form.values.dayDiff}</Text>
                </Group>
              }
              value={form.values.appointmentEnd ? form.values.appointmentEnd.format('HH:mm') : ''}
              disabled
            />
          </Group>
        </SimpleGrid>
        <Group pt='xs'>
          <Stack>
            <Group>
              <Checkbox checked label='Re-occurring Event' disabled />
            </Group>

            <Stack pl='md'>
              <Radio.Group
                label='Recurrence Type'
                data-test='recurrence-type'
                value={form.values.frequency}
                onChange={handleFrequencyChange}
              >
                <Group grow align='stretch' pt='xs'>
                  {(
                    [
                      { value: 'weekly', title: 'Weekly', subtitle: 'Every 7 days' },
                      { value: 'biweekly', title: 'Biweekly', subtitle: 'Every 14 days' },
                      { value: 'monthly', title: 'Monthly', subtitle: 'Same date each calendar month' },
                    ] as const
                  ).map(option => (
                    <Paper key={option.value} withBorder radius='md' p='md'>
                      <Radio
                        value={option.value}
                        data-test={`recurrence-${option.value}`}
                        labelPosition='left'
                        styles={{ body: { width: '100%', justifyContent: 'space-between' } }}
                        label={
                          <Stack spacing={2}>
                            <Text fw={600}>{option.title}</Text>
                            <Text size='sm' color='dimmed'>
                              {option.subtitle}
                            </Text>
                          </Stack>
                        }
                      />
                    </Paper>
                  ))}
                </Group>
              </Radio.Group>
              {form.values.frequency !== 'monthly' && (
                <Checkbox.Group label='Days of the week' {...form.getInputProps('days')}>
                  <Group spacing='lg'>
                    <Checkbox
                      data-test='sunday'
                      value='SU'
                      label='Sun'
                      disabled={dayjs(form.values.startDate).day() === 0}
                    />
                    <Checkbox
                      data-test='monday'
                      value='MO'
                      label='Mon'
                      disabled={dayjs(form.values.startDate).day() === 1}
                    />
                    <Checkbox
                      data-test='tuesday'
                      value='TU'
                      label='Tues'
                      disabled={dayjs(form.values.startDate).day() === 2}
                    />
                    <Checkbox
                      data-test='wednesday'
                      value='WE'
                      label='Wed'
                      disabled={dayjs(form.values.startDate).day() === 3}
                    />
                    <Checkbox
                      data-test='thursday'
                      value='TH'
                      label='Thu'
                      disabled={dayjs(form.values.startDate).day() === 4}
                    />
                    <Checkbox
                      data-test='friday'
                      value='FR'
                      label='Fri'
                      disabled={dayjs(form.values.startDate).day() === 5}
                    />
                    <Checkbox
                      data-test='saturday'
                      value='SA'
                      label='Sat'
                      disabled={dayjs(form.values.startDate).day() === 6}
                    />
                  </Group>
                </Checkbox.Group>
              )}
              <Group grow align='top'>
                <DateInput
                  {...form.getInputProps('endDate')}
                  label='End Date'
                  placeholder='Never'
                  data-test='endDate'
                  minDate={form.values.startDate}
                  clearable
                />
                <div />
              </Group>
            </Stack>
          </Stack>
        </Group>

        <Divider mt='lg' mb={0} mx='-xl' />

        <ExpandableSection
          title='Additional Settings'
          opened={settingsOpened}
          handleToggle={handleSettingsToggle}
        >
          <Stack spacing='sm' px='md'>
            <Input.Wrapper>
              <SimpleGrid cols={1}>
                <Switch
                  my='xs'
                  label='Allow Late Cancellation'
                  checked={form.values?.cancellationSettings?.allowLateCancellation || false}
                  {...form.getInputProps('cancellationSettings.allowLateCancellation')}
                />
              </SimpleGrid>
            </Input.Wrapper>
            <Input.Wrapper
              label={
                form.values.cancellationSettings.allowLateCancellation
                  ? 'Time before the start of an appointment when a cancellation is considered late'
                  : 'Time before the start of an appointment when cancellations are no longer allowed'
              }
            >
              <SimpleGrid cols={2}>
                <NumberInput
                  min={0}
                  max={setMaxLateCancellationIntervalCount(
                    form.values.cancellationSettings.cancellationInterval,
                  )}
                  {...form.getInputProps('cancellationSettings.cancellationIntervalCount')}
                  required
                />
                <Select
                  data={timeUnitOptions}
                  {...form.getInputProps('cancellationSettings.cancellationInterval')}
                />
              </SimpleGrid>
            </Input.Wrapper>

            {form.values.cancellationSettings.allowLateCancellation && (
              <>
                <Group noWrap>
                  <Switch
                    my='xs'
                    checked={form.values?.cancellationSettings?.useLateCancellationFee || false}
                    {...form.getInputProps('cancellationSettings.useLateCancellationFee')}
                  />
                  <Text size='sm'>Auto-Charge Fee for Late Cancellations</Text>
                  {form.values.cancellationSettings.useLateCancellationFee && (
                    <MoneyField
                      country={country}
                      min={0}
                      {...form.getInputProps('cancellationSettings.cancellationFee')}
                    />
                  )}
                </Group>
                <Input.Wrapper>
                  <SimpleGrid cols={1}>
                    <Switch
                      my='xs'
                      label='Deduction of credit for late cancellations'
                      checked={form.values?.cancellationSettings?.cancellationCreditDeduction || false}
                      {...form.getInputProps('cancellationSettings.cancellationCreditDeduction')}
                    />
                  </SimpleGrid>
                </Input.Wrapper>
              </>
            )}
            <Divider />
            <Group noWrap>
              <Switch
                my='xs'
                checked={form.values?.cancellationSettings?.useNoShowFee || false}
                {...form.getInputProps('cancellationSettings.useNoShowFee')}
              />
              <Text size='sm'>Auto-Charge Fee for No Shows</Text>
              {form.values.cancellationSettings.useNoShowFee && (
                <MoneyField
                  country={country}
                  min={0}
                  {...form.getInputProps('cancellationSettings.noShowFee')}
                />
              )}
            </Group>
            <Input.Wrapper>
              <SimpleGrid cols={1}>
                <Switch
                  my='xs'
                  label='Deduction of credit for No Shows'
                  checked={form.values?.cancellationSettings?.noShowCreditDeduction || false}
                  {...form.getInputProps('cancellationSettings.noShowCreditDeduction')}
                />
              </SimpleGrid>
            </Input.Wrapper>
          </Stack>
        </ExpandableSection>
      </Stack>
    </form>
  );
};

export default DetailsEditAllFutureAppointmentsForm;
